import React from "react";
import { Table, Avatar, Typography, Tag, Tooltip, Space } from "antd";
import {
  MailOutlined,
  GlobalOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import UserActions from "./UserActions";
import UserRoleTag from "./UserRoleTag";
import UserStatusTag from "./UserStatusTag";

const { Title, Text } = Typography;

const UserTable = ({
  currentUser,
  users,
  loading,
  onViewUser,
  onEditUser,
  onChangeUserStatus,
  onImpersonate,
  onAssignPlan,
  pagination,
  onPageChange,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    /* =========================
        USER INFO
    ========================== */
    {
      title: "USER",
      key: "user",
      width: "25%",
      render: (_, record) => (
        <Space align="start">
          <Avatar
            size="large"
            style={{
              backgroundColor: record.is_verified ? "#52c41a" : "#faad14",
            }}
          >
            {record.full_name?.charAt(0) || "U"}
          </Avatar>

          <Space direction="vertical" size={0}>
            <Title level={5} style={{ margin: 0 }}>
              {record.full_name}
            </Title>

            <Text type="secondary">
              <MailOutlined /> {record.email}
            </Text>

            <Text type="secondary" style={{ fontSize: 12 }}>
              ID: {record._id?.substring(0, 8)}...
            </Text>
          </Space>
        </Space>
      ),
    },

    /* =========================
        ROLE & STATUS
    ========================== */
    {
      title: "ROLE & STATUS",
      key: "status",
      width: "20%",
      render: (_, record) => (
        <Space direction="vertical">
          <UserRoleTag role={record.role} />
          <UserStatusTag user={record} />

          {record.last_login_at && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last login: {formatDate(record.last_login_at)}
            </Text>
          )}
        </Space>
      ),
    },

    /* =========================
        WEBSITE
    ========================== */
    {
      title: "WEBSITE",
      key: "website",
      width: "20%",
      render: (_, record) => (
        <Space direction="vertical">
          {record.website_url ? (
            <Tooltip title={record.website_url}>
              <Space>
                <GlobalOutlined />
                <a
                  href={record.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {record.website_url.length > 30
                    ? `${record.website_url.substring(0, 30)}...`
                    : record.website_url}
                </a>
              </Space>
            </Tooltip>
          ) : (
            <Text type="secondary">No website</Text>
          )}

          {record.selected_property && (
            <Tag color="cyan" icon={<LinkOutlined />}>
              Connected Property
            </Tag>
          )}
        </Space>
      ),
    },

    /* =========================
        ACTIONS
    ========================== */
    {
      title: "ACTIONS",
      key: "actions",
      width: "20%",
      render: (_, record) => (
        <UserActions
          currentUser={currentUser}
          user={record}
          onView={() => onViewUser(record)}
          onEdit={() => onEditUser(record)}
          onChangeStatus={(status) =>
            onChangeUserStatus(record, status)
          }
          onImpersonate={() => onImpersonate(record._id)}
          onAssignPlan={() => onAssignPlan(record)}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={users}
      loading={loading}
      rowKey="_id"
      pagination={{
        pageSize: 10,
        ...(pagination || {}),
        showSizeChanger: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} users`,
        ...(onPageChange ? { onChange: onPageChange } : {}),
      }}
      rowClassName={(record) =>
        record.status === "suspended" ? "suspended-row" : ""
      }
    />
  );
};

export default UserTable;
